import { Animation } from 'util.animations';
import EventHandler from 'util.events';
import debounce from 'vendor.just-debounce';

export class BackToTopButton extends HTMLElement {
  #label = null;
  #events = null;
  #buttonAnimation = null;
  #labelAnimation = null;
  #scrollThreshold = 0.3;

  constructor() {
    super();

    this.#label = this.querySelector('.back-to-top__button-label');
    this.#events = new EventHandler();
    this.#buttonAnimation = new Animation({ el: this });
    this.#labelAnimation = new Animation({ el: this.#label, state: 'hidden' });

    this.#setupEventListeners();
  }

  get scrollPosition() {
    return window.scrollY / (document.body.offsetHeight - window.innerHeight);
  }

  #setupEventListeners() {
    this.#events.register(this, 'click', () => this.#scrollToTop());
    this.#events.register(this, 'mouseenter', () => this.#onHover());
    this.#events.register(this, 'mouseleave', () => this.#onHoverEnd());
    this.#events.register(
      window,
      'scroll',
      debounce(() => this.#onScroll(), 100, true)
    );
  }

  #onScroll() {
    if (window.PXUTheme.media_queries.medium.matches) return;

    if (this.scrollPosition >= this.#scrollThreshold) {
      this.#buttonAnimation.animateTo('visible');
    } else {
      this.#buttonAnimation.animateTo('hidden');
    }
  }

  #onHover() {
    if (window.PXUTheme.media_queries.medium.matches) return;
    this.#labelAnimation.animateTo('visible', {
      onStart: ({ el }) => {
        // When this function is called, element is `display: block; width: 0;`
        // but the wrapper's scrollWidth is the width we want to transition to
        const { scrollWidth } = el;

        // Add 8px to the width to create padding between the text and the chevron
        // We add this space here because adding padding via CSS causes a 'stepped'
        // animation effect due to the way browsers calculate visible width
        const adjustedScrollWidth = parseInt(scrollWidth, 10) + 8;
        el.style.setProperty('--open-width', `${adjustedScrollWidth}px`);
      },
    });
  }

  #onHoverEnd() {
    if (window.PXUTheme.media_queries.medium.matches) return;
    this.#labelAnimation.animateTo('hidden');
  }

  #scrollToTop() {
    document.activeElement.blur();
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  disconnectedCallback() {
    this.#events.unregisterAll();
    this.#buttonAnimation.unload();
    this.#labelAnimation.unload();
  }
}

customElements.define('back-to-top-button', BackToTopButton);
